import { useState } from "react";
import { ChevronRight, BookOpen, Zap, MessageSquare, Phone, Mail, Calendar, Users, BarChart3, Bot, Sparkles, Plug, Shield, FileText } from "lucide-react";

/**
 * E2E user guide rendered as a structured doc. Sidebar TOC + scroll-anchor
 * sections. Intentionally avoids a markdown library to keep the dependency
 * tree small — the content rarely changes.
 *
 * The same content also lives at /app/memory/USER_GUIDE.md (kept in sync by
 * hand). Use that file when explaining flows to AI assistants / new team
 * members offline.
 */
export default function Help() {
  const sections = [
    { id: "quickstart",   label: "Quick Start (5 min)",       icon: Zap },
    { id: "campaigns",    label: "Campaigns + ICP",            icon: FileText },
    { id: "scraper",      label: "Lead Discovery (Scraper)",   icon: Users },
    { id: "ai",           label: "AI Personalization",         icon: Bot },
    { id: "sequences",    label: "Multi-Touch Sequences",      icon: ChevronRight },
    { id: "sms",          label: "SMS (Twilio + Quo)",         icon: MessageSquare },
    { id: "calls",        label: "Voice Calls (Vapi)",         icon: Phone },
    { id: "email",        label: "Email (SendGrid)",           icon: Mail },
    { id: "demo-autobook",label: "Demo-Auto-Book",             icon: Calendar },
    { id: "angles",       label: "Custom AI Angles",           icon: Sparkles },
    { id: "mcp",          label: "MCP Server",                 icon: Plug },
    { id: "analytics",    label: "Analytics + ROI",            icon: BarChart3 },
    { id: "compliance",   label: "Compliance (TCPA, STOP)",    icon: Shield },
  ];
  const [active, setActive] = useState("quickstart");

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="help-page">
      <div className="mb-8 flex items-start gap-4">
        <div className="w-12 h-12 bg-[#002FA7] text-white flex items-center justify-center shrink-0">
          <BookOpen className="w-6 h-6" />
        </div>
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">User Guide</h1>
          <p className="text-zinc-600 mt-2 max-w-2xl text-sm">
            Everything you need to take a cold local-business lead → booked demo. Pinned to the bottom of every section: the exact API endpoints + UI buttons that actually do the work.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-8">
        {/* Table of contents */}
        <nav className="md:sticky md:top-6 md:self-start space-y-1" data-testid="help-toc">
          {sections.map((s) => (
            <a
              key={s.id} href={`#${s.id}`}
              onClick={() => setActive(s.id)}
              className={`flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider border-l-2 transition-colors ${
                active === s.id
                  ? "border-[#002FA7] text-[#002FA7] bg-blue-50/50"
                  : "border-transparent text-zinc-600 hover:text-[#002FA7] hover:border-zinc-300"
              }`}
              data-testid={`help-toc-${s.id}`}
            >
              <s.icon className="w-3.5 h-3.5" />
              {s.label}
            </a>
          ))}
        </nav>

        <div className="space-y-12 max-w-3xl">
          <Section id="quickstart" title="Quick Start (5 minutes)">
            <Ol>
              <li><b>Settings → Twilio / Quo</b>: Choose your SMS provider and paste your API key + phone. (Twilio works out of the box; Quo needs key + number + webhook secret.)</li>
              <li><b>Settings → Vapi</b>: Paste your Vapi Assistant ID and Phone Number ID so the AI agent can place calls.</li>
              <li><b>Settings → SendGrid</b>: Paste your SendGrid API key + verified From address. Required only if you want email steps.</li>
              <li><b>Settings → Google Calendar</b>: Click "Connect" to authorize the AI to book demos straight into your calendar.</li>
              <li><b>Campaigns → New</b>: Pick a niche, set a value prop, lock the ICP filter, save.</li>
              <li><b>Scraper → Search</b>: Find local businesses for the campaign. Pick the best ones → "Push to Pipeline".</li>
              <li><b>Leads → Pipeline</b>: AI auto-generates SMS/call scripts. Flip the campaign to <i>Active</i> and the sequence starts immediately.</li>
            </Ol>
            <Tip>Use <b>Settings → Test SMS / Test email / Test call</b> to verify every channel works before activating a real campaign.</Tip>
          </Section>

          <Section id="campaigns" title="Campaigns + Ideal Customer Profile (ICP)">
            <P>A campaign bundles together a <b>niche</b>, a <b>value prop</b>, a <b>cadence</b>, and an <b>ICP filter</b>. The ICP is what tells the AI <em>not</em> to push a business that's missing a website, lacks reviews, etc.</P>
            <Ul>
              <li><b>Where</b>: <code>/campaigns</code> → "New campaign"</li>
              <li><b>What to set</b>: industry, value proposition, target geography, and the ICP filters (min rating, min review count, must-have-website, etc.)</li>
              <li><b>How it shows up</b>: When the Scraper runs, leads outside your ICP are tagged <code>icp_mismatch</code> and never get auto-contacted.</li>
            </Ul>
            <Tip>You can override the global cadence per campaign — useful when wedding venues need 7 SMS + 3 calls but restaurants only need 3 calls.</Tip>
          </Section>

          <Section id="scraper" title="Lead Discovery (Scraper)">
            <P>Two data sources, one inbox:</P>
            <Ul>
              <li><b>Google Places</b>: best for local brick-and-mortar (restaurants, gyms, retail). Free quota, broad coverage.</li>
              <li><b>Apollo.io (B2B)</b>: best for SaaS / agencies — title-based search, decision-maker emails, technographics.</li>
            </Ul>
            <P>Search results land in the Scraper drawer. Click "Verify" to enrich a row (extra Google Place call → fills hours, phone, website). Multi-select → <b>Push to Pipeline</b> creates leads tied to the campaign you picked.</P>
            <Tip>Bulk CSV import works the same way: <code>/scraper</code> → "Import CSV". Headers must include <code>name, phone, email, city, state</code> at minimum.</Tip>
          </Section>

          <Section id="ai" title="AI Personalization (Claude Sonnet 4.5)">
            <P>Every lead pushed into a campaign goes through Claude. Claude returns:</P>
            <Ul>
              <li><code>icp_match_score</code> (0-100) + a short reason</li>
              <li>A <code>call_script</code> tailored to the business (≤25 words opener)</li>
              <li>An <code>sms_template</code> (≤320 chars, CTA, no emojis)</li>
              <li>An <code>email_template</code> (subject + body, used only if you add an email step)</li>
            </Ul>
            <P>The <b>Settings → AI Prompts</b> page lets you override the system prompt, call rules, SMS rules, and email rules. Leave fields blank to use the defaults.</P>
            <Tip>Re-classify any lead from its detail page → "Re-run AI" to test prompt edits.</Tip>
          </Section>

          <Section id="sequences" title="Multi-Touch Sequences">
            <P>A sequence is a list of steps. Each step has:</P>
            <Ul>
              <li><b>Day offset</b> + <b>hour/minute</b> (local to the lead's state)</li>
              <li><b>Kind</b>: <code>sms</code> | <code>call</code> | <code>email</code> | <code>mark_lost</code></li>
              <li><b>Angle</b>: an AI hint that changes the tone of that touch (e.g. <code>intro</code> vs <code>breakup</code>)</li>
            </Ul>
            <P>Steps slide forward to the next valid business hour if they'd otherwise land outside <b>Settings → Outreach Cadence → Business hours</b>.</P>
            <Tip>Hit "Try" on any step in the editor to dry-run that touch through Claude (no SMS sent) — perfect for previewing tone changes.</Tip>
          </Section>

          <Section id="sms" title="SMS — Twilio or Quo">
            <P>The <b>SMS Provider</b> radio in Settings picks the outbound channel. Both providers route inbound replies into the lead's timeline.</P>
            <Ul>
              <li><b>Twilio</b>: classic, A2P 10DLC ready. Status callbacks update delivery state.</li>
              <li><b>Quo</b>: cleaner pricing for low volume. Webhook signed with HMAC-SHA256.</li>
            </Ul>
            <P>For Quo, copy the URL from Settings → "Webhook URL" and paste it into your Quo webhook config. Set events to <code>message.received</code>, <code>message.delivered</code>.</P>
            <Tip>Vapi voice calls are independent of this setting — Vapi uses its own managed Twilio number behind the scenes.</Tip>
          </Section>

          <Section id="calls" title="Voice Calls (Vapi)">
            <P>The AI agent ("Sara" by default) calls leads using a Vapi assistant. The call uses a script <em>generated for that specific lead</em>, then sends back a transcript when finished.</P>
            <Ul>
              <li><b>Settings → Vapi Assistant ID</b>: your Vapi assistant (we recommend an "outbound qualifier" persona).</li>
              <li><b>Settings → Vapi Phone Number ID</b>: a number imported from Twilio — Vapi-managed numbers have low daily limits.</li>
              <li><b>Webhook sync</b>: hit "Sync Vapi webhook" once so call results land in the lead timeline.</li>
            </Ul>
            <Tip>If Auto-Qualify is on, Claude reads the transcript and moves the lead to <code>qualified</code> or <code>lost</code> automatically.</Tip>
          </Section>

          <Section id="email" title="Email — SendGrid">
            <P>Add an <code>email</code> step to any cadence to send personalized email alongside SMS and calls. The Settings page exposes:</P>
            <Ul>
              <li>API key + From address + From name + Reply-to</li>
              <li><b>Inbound Parse URL</b> — paste this into SendGrid (Settings → Inbound Parse) and bind it to an MX record (e.g. <code>reply.scalater.com</code>).</li>
            </Ul>
            <P>Replies arrive as <code>email</code> activities on the matching lead. Claude classifies the reply (interested / not interested / unsubscribe) just like an SMS reply.</P>
            <Tip>Always run <b>Test email</b> to a Gmail-style mailbox first; if it bounces, your domain auth (DKIM/SPF) probably isn't set in SendGrid yet.</Tip>
          </Section>

          <Section id="demo-autobook" title="Demo-Auto-Book">
            <P>When a prospect replies "yes" or "interested" by SMS, the system can auto-generate a one-time booking link and text it back. They pick a slot on a public page; it lands on your Google Calendar.</P>
            <Ol>
              <li>Toggle <b>Auto-Book Demos</b> on in any campaign.</li>
              <li>Make sure Google Calendar is connected in Settings + your working hours are set.</li>
              <li>When a positive reply comes in (with Auto-Qualify ≥ 60%), the SMS auto-replies with a <code>/book/&lt;token&gt;</code> URL.</li>
            </Ol>
            <Tip>The slot list respects both your <b>working hours</b> and your Google Calendar's <em>busy</em> times — no double-booking.</Tip>
          </Section>

          <Section id="angles" title="Custom AI Angles">
            <P>An "angle" is a short directive that changes how Claude writes a specific touch. Built-in angles cover the basics (<code>intro</code>, <code>value</code>, <code>breakup</code>...). You can layer your own in Settings → Custom AI Angles.</P>
            <Ul>
              <li><b>Key</b>: kebab-case identifier (e.g. <code>case-study-bakery</code>)</li>
              <li><b>Label</b>: shown in the campaign builder dropdown</li>
              <li><b>Directive</b>: literal text appended to Claude's prompt for that touch</li>
            </Ul>
            <P>Reuse a built-in key (e.g. <code>intro</code>) to override the built-in directive for this workspace.</P>
            <Tip>Best use case: encode case-study mentions or vertical-specific proof points so step #3 of every campaign automatically references the right success story.</Tip>
          </Section>

          <Section id="mcp" title="MCP Server (Claude / Cursor / Continue.dev)">
            <P>Every workspace can mint MCP tokens at <code>/mcp-access</code>. Plug the URL into your Claude Desktop / Cursor config and you get tools like <code>list_leads</code>, <code>scrape_places</code>, and <code>start_campaign</code> right from your IDE.</P>
            <Tip>Tokens are scoped per workspace. Revoke + rotate them whenever someone leaves the team.</Tip>
          </Section>

          <Section id="analytics" title="Analytics + ROI">
            <P>The Analytics dashboard tracks reply rate, qualified rate, booked-demo rate, and revenue (if you set deal values on closed leads). It also surfaces "needs-attention" alerts — campaigns burning credits without conversions get flagged in amber.</P>
            <Tip>Use the date-range picker to compare week-over-week. The "Top Performing Angles" widget is gold for prompt iteration.</Tip>
          </Section>

          <Section id="compliance" title="Compliance — TCPA + STOP">
            <P>Before any outbound SMS, you must check the <b>TCPA Acknowledged</b> box in Settings. The system blocks sequence activation otherwise.</P>
            <Ul>
              <li>Every outbound SMS includes "Reply STOP to opt out" footer (handled by both Twilio and Quo).</li>
              <li>Inbound STOP / UNSUBSCRIBE replies immediately suppress further outbound for that phone number.</li>
              <li>Auto-Qualify in <code>auto</code> mode also moves the lead to <code>lost</code> on STOP.</li>
            </Ul>
            <Tip>Keep an eye on the carrier compliance section in Twilio / Quo — you'll want an A2P 10DLC campaign registered before high volume.</Tip>
          </Section>

          <div className="border-t border-zinc-200 pt-6 mt-8">
            <p className="text-xs text-zinc-500">
              Missing something? Open an issue in the deployment repo, or ping the team. This guide is also versioned at <code>/app/memory/USER_GUIDE.md</code>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-6" data-testid={`help-section-${id}`}>
      <h2 className="font-heading text-2xl font-bold mb-4 tracking-tight">{title}</h2>
      <div className="space-y-3 text-sm text-zinc-700 leading-relaxed">{children}</div>
    </section>
  );
}

const P = ({ children }) => <p>{children}</p>;
const Ul = ({ children }) => <ul className="list-disc pl-5 space-y-1.5">{children}</ul>;
const Ol = ({ children }) => <ol className="list-decimal pl-5 space-y-1.5">{children}</ol>;
function Tip({ children }) {
  return (
    <div className="border-l-4 border-[#002FA7] bg-blue-50/50 px-4 py-3 text-xs text-zinc-700">
      <span className="font-bold uppercase tracking-wider text-[#002FA7] mr-2">Tip</span>
      {children}
    </div>
  );
}
